import { forwardRef, useEffect, useImperativeHandle, useState } from "react";

import { ShowWhen } from "../show-when/show-when";
import { OptionType } from "./select";

import { MagnifyingGlass } from "@/components/assets/icons";
import { SelectOptionButton } from "./select-option-button";
import { Button } from "../button/button";
import { useTranslations } from "next-intl";

interface TSelectOptions {
  options: OptionType[];
  selectedOption?: OptionType;
  onSelect: (option) => void;
  iconFit?: "cover" | "contain" | "scale-down";
  hideSearch?: boolean;
  searchPlaceholder?: string;
  additionalFormButtonText?: string;
  onAdditionalButtonClick?: () => void;
  takeSearchValue?: (searchValue: string) => void;
  translation?: boolean;
}

export interface SelectOptionsHandle {
  resetSearch: () => void;
}

const SelectOptions = forwardRef<SelectOptionsHandle, TSelectOptions>(
  (
    {
      options,
      selectedOption,
      onSelect,
      iconFit,
      hideSearch = false,
      searchPlaceholder,
      additionalFormButtonText,
      onAdditionalButtonClick,
      takeSearchValue,
      translation,
    }: TSelectOptions,
    ref,
  ) => {
    const t = useTranslations();
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredOptions, setFilteredOptions] = useState<OptionType[]>([]);

    useImperativeHandle(ref, () => ({
      resetSearch: () => {
        setSearchQuery("");
        setFilteredOptions(options);
      },
    }));

    const handleSearch = (event: any) => {
      const query = event.target.value.toLowerCase();
      setSearchQuery(query);
      setFilteredOptions(
        options.filter(
          (option) =>
            //@ts-ignore
            option?.secondaryText
              ?.toLowerCase()
              .includes(query?.toLowerCase()) ||
            option?.mainText?.toLowerCase()?.includes(query?.toLowerCase()),
        ),
      );
      if (takeSearchValue) {
        takeSearchValue(query);
      }
    };

    useEffect(() => {
      if (searchQuery) {
        setFilteredOptions(
          options.filter(
            (option) =>
              //@ts-ignore
              option?.secondaryText
                ?.toLowerCase()
                .includes(searchQuery.toLowerCase()) ||
              option?.mainText
                ?.toLowerCase()
                ?.includes(searchQuery.toLowerCase()),
          ),
        );
      } else {
        setFilteredOptions(options);
      }
    }, [options]);

    return (
      <>
        <ShowWhen condition={!hideSearch}>
          <div className="mx-[10px] mt-[10px] flex items-center gap-[6px] border-b-[1px] border-zinc-100 pb-[7px]">
            <div className="flex h-[24px] w-[24px] items-center justify-center">
              <MagnifyingGlass />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              placeholder={searchPlaceholder || "Search"}
              className="w-full text-xs font-medium placeholder:text-xs placeholder:text-[#00000080] focus:outline-none active:outline-none"
            />
          </div>
        </ShowWhen>

        <ul>
          {filteredOptions?.map((option, i) => {
            return (
              <SelectOptionButton
                key={i}
                option={option}
                handleSelect={onSelect}
                iconFit={iconFit}
                selectedOption={selectedOption}
                translation={translation}
              />
            );
          })}
        </ul>
        <ShowWhen condition={!!onAdditionalButtonClick}>
          <div className="border-top sticky bottom-0 mt-[15px] w-full border-t-[1px] border-[#f3f3f3] bg-white p-[15px]">
            <Button type={"button"} onClick={onAdditionalButtonClick}>
              {additionalFormButtonText || t("add_new")}
            </Button>
          </div>
        </ShowWhen>
      </>
    );
  },
);

SelectOptions.displayName = "SelectOptions";

export { SelectOptions };
